import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Pencil } from 'lucide-react';
import Modal from './Modal';
import CheckboxMultiSelect from './CheckboxMultiSelect';
import { fetchStaffDetailsList, createStaffDetails, updateStaffDetails } from '../api/staffDetailsApi';
import { fetchMenus } from '../api/apiMetadataApi';
import { formatDate, formatUserName } from '../utils/formatters';
import './StaffDetails.css';

// Kept for consistency with SourceInfo.js's duplicate-detection helper, in
// case a 200 OK response ever embeds a 0/duplicate sentinel. In practice the
// backend signals a duplicate with a real HTTP 409 (see the catch block in
// handleSave), which is the actually-reachable path today.
const isDuplicateRecordResult = (responseData) => {
  if (responseData === 0) return true;
  if (responseData && typeof responseData === 'object') {
    return [responseData.result, responseData.Result, responseData.id, responseData.Id].some(
      (v) => v === 0
    );
  }
  return false;
};

// dbo.Menu.MenuKey of Staff Details, which is granted by IsOwner rather than Menu Access.
const OWNER_MENU_KEY = 'StaffDetails';

const EMAIL_RE =/^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  azureAD: '',
  email: '',
  menusAccess: [],
  isActive: true,
  isOwner: false,
};

// Menu access is stored as comma-separated dbo.Menu ids (StaffMenuPermission.MenusAccess),
// e.g. "1,3,5" <-> [1, 3, 5]. Anything that isn't a positive integer is ignored.
const parseMenusAccess = (value) =>
  String(value ?? '')
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);

const formatMenusAccess = (ids) => (ids.length > 0 ? [...ids].sort((a, b) => a - b).join(',') : null);

function StaffDetails({ accessToken }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  // { message, type: 'warning' | 'error' | 'success' } | null
  const [toast, setToast] = useState(null);
  // Menu Access options: [{ value: menuId, label }] from dbo.Menu.
  const [menuOptions, setMenuOptions] = useState([]);
  const [menusLoading, setMenusLoading] = useState(true);

  // Auto-dismiss the toast a few seconds after it's shown; also cleans up
  // the timer if a new toast replaces it or the component unmounts.
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const loadStaffDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchStaffDetailsList();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch staff details');
      console.error('StaffDetails error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStaffDetails();
  }, [loadStaffDetails]);

  // A failure here only leaves the Menu Access list empty; the rest of the page still works.
  // Staff Details is never an assignable menu (it comes from IsOwner); the API already
  // excludes it, and it's filtered here by MenuKey too in case an older API returns it.
  useEffect(() => {
    fetchMenus()
      .then((data) => setMenuOptions((Array.isArray(data) ? data : [])
        .filter((m) => m.menuKey !== OWNER_MENU_KEY)
        .map((m) => ({ value: m.id, label: m.label }))))
      .catch((err) => console.error('StaffDetails: failed to load menus', err))
      .finally(() => setMenusLoading(false));
  }, []);

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required.';
    if (!form.lastName.trim()) e.lastName = 'Last name is required.';
    if (!form.azureAD.trim()) e.azureAD = 'Azure AD id is required.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!EMAIL_RE.test(form.email.trim())) e.email = 'Please enter a valid email address.';
    return e;
  };

  const handleOpenAdd = () => {
    setEditingRecord(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setIsModalOpen(true);
  };

  const handleOpenEdit = (record) => {
    // editingRecord is kept as-opened so the update request always identifies
    // the original record (by Id) regardless of what the user changes on screen.
    setEditingRecord(record);
    setForm({
      firstName: record.firstName || '',
      lastName: record.lastName || '',
      azureAD: record.azureAD || '',
      email: record.email || '',
      menusAccess: parseMenusAccess(record.menusAccess),
      isActive: !!record.isActive,
      isOwner: !!record.isOwner,
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setErrors({});
    setIsModalOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      // Switching IsOwner on selects every available menu (Staff Details is never one of
      // them); switching it off clears the selection. Either way the admin can still change
      // the menus before saving. Only an actual switch does this, so opening an existing
      // record for Edit always shows its saved menus.
      if (name === 'isOwner' && checked !== prev.isOwner) {
        next.menusAccess = checked ? menuOptions.map((o) => o.value) : [];
      }
      return next;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleMenusAccessChange = (ids) => {
    setForm((prev) => ({ ...prev, menusAccess: ids }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) { setErrors(fieldErrors); return; }
    setErrors({});

    setSaving(true);

    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      azureAD: form.azureAD,
      email: form.email,
      menusAccess: formatMenusAccess(form.menusAccess),
      isActive: form.isActive,
      isOwner: form.isOwner,
    };

    try {
      if (editingRecord) {
        const updateResult = await updateStaffDetails(editingRecord.id, payload);
        if (isDuplicateRecordResult(updateResult)) {
          setToast({ message: 'Record already exists.', type: 'warning' });
          await loadStaffDetails();
          setSaving(false);
          return;
        }
        // The endpoint only returns { statusCode, message }, not the updated
        // record, so refresh from the server to reflect what actually persisted.
        await loadStaffDetails();
      } else {
        const created = await createStaffDetails(payload);
        if (isDuplicateRecordResult(created)) {
          setToast({ message: 'Record already exists.', type: 'warning' });
          await loadStaffDetails();
          setSaving(false);
          return;
        }
        // Database-confirmed insert: append using what the backend actually
        // returned (falls back to the submitted payload for any field it
        // didn't echo back), not a blind optimistic guess.
        setRecords((prev) => [...prev, { id: created?.id, ...payload, ...created }]);
      }
      setSaving(false);
      setIsModalOpen(false);
      setToast({ message: editingRecord ? 'Record updated successfully.' : 'Record saved successfully.', type: 'success' });
    } catch (err) {
      console.error('StaffDetails: save failed', err.response?.data || err.message);

      // The backend signals "already exists" with a real HTTP 409 Conflict
      // (see StaffDetailsController), so a duplicate lands here, not in the
      // isDuplicateRecordResult branch above. Surface it the same way.
      if (err.response?.status === 409) {
        setToast({ message: 'Record already exists.', type: 'warning' });
        await loadStaffDetails();
        setSaving(false);
        return;
      }

      // ASP.NET Core's default model-validation failure shape is
      // { title: "One or more validation errors occurred.", errors: { Field: ["reason"] } } —
      // surface the per-field detail too, not just the generic title.
      const validationErrors = err.response?.data?.errors;
      const validationDetail = validationErrors
        ? Object.entries(validationErrors)
            .map(([field, messages]) => `${field}: ${[].concat(messages).join(' ')}`)
            .join('\n')
        : null;
      const serverMessage =
        validationDetail ||
        (typeof err.response?.data === 'string' && err.response.data) ||
        err.response?.data?.message ||
        err.response?.data?.title ||
        err.message ||
        'Failed to save staff details';
      setToast({ message: `Save failed: ${serverMessage}`, type: 'error' });
      setSaving(false);
    }
  };

  return (
    <section className="staff-details-section">
      {toast && (
        <div className={`toast toast--${toast.type}`} role="alert">
          <span className="toast__icon">
            {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
          </span>
          <span className="toast__message">{toast.message}</span>
          <button type="button" className="toast__close" onClick={() => setToast(null)}>&times;</button>
        </div>
      )}

      <div className="container">
        <div className="sd-header d-flex">
          <h4 className="sd-title">Staff Details</h4>
          <button type="button" className="btn btn-primary sd-add-btn" onClick={handleOpenAdd}>
            Add New
          </button>
        </div>

        {loading ? (
          <div className="sd-loader-wrap w-full justify-content-center">
            <div className="spinner"></div>
            <p className="text text-center">Loading Staff Details...</p>
          </div>
        ) : error ? (
          <div className="sd-msg-center">
            <div className="warning-wrap alert-msg-box">
              <span className="icon warning-icon"><AlertTriangle size={20} /></span>
              <span className="alert-title">Error: {error}</span>
              <button type="button" className="btn btn-danger sd-retry-btn" onClick={loadStaffDetails}>Retry</button>
            </div>
          </div>
        ) : records.length === 0 ? (
          <div className="sd-msg-center">
            <div className="warning-wrap alert-msg-box">
              <span className="icon warning-icon"><AlertTriangle size={20} /></span>
              <span className="alert-title">No staff details records found</span>
            </div>
          </div>
        ) : (
          <div className="content-box">
            <div className="sd-table-wrap data-table-wrap">
              <table className="sd-table data-table">
                <thead>
                  <tr>
                    <th className="text-center">Actions</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Azure AD</th>
                    <th>Email</th>
                    <th className="text-center">IsActive</th>
                    <th className="text-center">IsOwner</th>
                    <th>Created By</th>
                    <th>Created On</th>
                    <th>Updated By</th>
                    <th>Updated On</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => (
                    <tr key={record.id} className="sd-row">
                      <td className="text-center">
                        <button type="button" className="link__primary" onClick={() => handleOpenEdit(record)}>
                          <Pencil size={14} /><span>Edit</span>
                        </button>
                      </td>
                      <td>{record.firstName}</td>
                      <td>{record.lastName}</td>
                      <td>{record.azureAD}</td>
                      <td>{record.email}</td>
                      <td className="text-center">
                        <span className={record.isActive ? 'badge badge-success' : 'badge badge-muted'}>
                          {record.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className={record.isOwner ? 'badge badge-success' : 'badge badge-muted'}>
                          {record.isOwner ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>{formatUserName(record.createdBy)}</td>
                      <td>{formatDate(record.createdOn)}</td>
                      <td>{formatUserName(record.updatedBy)}</td>
                      <td>{formatDate(record.updatedOn)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal}
        title={editingRecord ? 'Edit Staff Details' : 'Add Staff Details'} variant="default" dialogClass="modal__dialog--md" >
        <form className="sd-form" onSubmit={handleSave} noValidate>
          {saving && (
            <div className="modal-saving-overlay">
              <span className="dots-loader"><span></span><span></span><span></span></span>
              <span className="saving-label">{editingRecord ? 'Updating...' : 'Saving...'}</span>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">First Name</label>
            <input
              className={`form-input ${errors.firstName ? ' form-input--error' : ''}`}
              type="text"
              name="firstName"
              value={form.firstName}
              onChange={handleFormChange}
            />
            {errors.firstName && <span className="form-error">{errors.firstName}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Last Name</label>
            <input
              className={`form-input ${errors.lastName ? ' form-input--error' : ''}`}
              type="text"
              name="lastName"
              value={form.lastName}
              onChange={handleFormChange}
            />
            {errors.lastName && <span className="form-error">{errors.lastName}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Azure AD</label>
            <input
              className={`form-input ${errors.azureAD ? ' form-input--error' : ''}`}
              type="text"
              name="azureAD"
              value={form.azureAD}
              onChange={handleFormChange}
            />
            {errors.azureAD && <span className="form-error">{errors.azureAD}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              className={`form-input ${errors.email ? ' form-input--error' : ''}`}
              type="email"
              name="email"
              value={form.email}
              onChange={handleFormChange}
            />
            {errors.email && <span className="form-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="sd-menus-access">Menu Access</label>
            <CheckboxMultiSelect
              inputId="sd-menus-access"
              options={menuOptions}
              value={form.menusAccess}
              onChange={handleMenusAccessChange}
              isLoading={menusLoading}
              placeholder="Select menus"
            />
          </div>

          <div className="form-group--checkbox">
            <div className="custom-toggle-switch">
              <input
                type="checkbox"
                id="chk-isActive"
                name="isActive"
                checked={form.isActive}
                onChange={handleFormChange}
              />
              <label htmlFor="chk-isActive">
                <span className="toggle-slider"></span>
                <span className="label-text">IsActive</span>
              </label>
            </div>

            <div className="custom-toggle-switch">
              <input
                type="checkbox"
                id="chk-isOwner"
                name="isOwner"
                checked={form.isOwner}
                onChange={handleFormChange}
              />
              <label htmlFor="chk-isOwner">
                <span className="toggle-slider"></span>
                <span className="label-text">IsOwner</span>
              </label>
            </div>
          </div>

          <div className="form-footer">
            <button type="button" className="btn btn-outline" onClick={handleCloseModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="dots-loader"><span></span><span></span><span></span></span> : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </section>
  );
}

export default StaffDetails;
